import React, {useState} from "react";
import MainLayout from "@/pages/auth/Login/components/MainLayout";
import {ReactComponent as LayoutImage} from "@/assets/images/UserLogin.svg"
import {
  Box,
  Grid, InputAdornment, TextField,
  Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {object, TypeOf, z} from "zod";
import WebURL from "@/urls";
import {useNavigate} from "react-router-dom";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useSnackbar} from "notistack";
import UserService from "@/services/auth/UserService";
import {LoadingButton} from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import CustomLink from "@/pages/auth/Login/components/CustomLink";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import FormControl from "@/pages/auth/Login/components/SignUp";
import LogoView from "./LogoView";
import {sleep} from "react-hook-form/dist/utils/sleep";
import IconButton from "@mui/material/IconButton";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

const ForgotPassword: React.FC = () => {
  const native = useNavigate();
  const {t} = useTranslation();
  const signUpSchema = object({
    email: z.string().max(50, t("common.The length of field cannot exceed digits", {number: 50})
    ).min(1, t("common.This field is required!")).email(t("common.Invalid email format")),
    verify_code: z.string().min(1, t("common.This field is required!")),
    new_password: z.string().min(1, t("common.This field is required!")),
    confirm_password: z.string().min(1, t("common.This field is required!")),
  });
  type signUpInput = TypeOf<typeof signUpSchema>;

  const [loading, setLoading] = useState<boolean>(false);
  const [codeLoading, setCodeLoading] = useState<boolean>(false);
  const {register, handleSubmit, getValues, trigger, formState: {errors}} = useForm<signUpInput>({
    resolver: zodResolver(signUpSchema)
  })

  const {enqueueSnackbar} = useSnackbar()
  const onForgotPwd = async (data: any) => {
    try {
      if (getValues('new_password') !== getValues('confirm_password')) {
        enqueueSnackbar(t("signUp.Confirm New Password does not match New Password"), {
          variant: "error",
          autoHideDuration: 1500,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        })
        return
      }
      setLoading(true)
      // console.log("onForgotPwd-->", data);
      const user = await UserService.forgotPwdV2(data).then((res) => {
        if (res.code == 'UASI0005') {
          enqueueSnackbar(t("common.success"), {
            variant: "success",
            autoHideDuration: 1500,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          })
          native(WebURL.AUTH_USER_LOGIN);
        } else if (res.code == 'UASE0033') {
          enqueueSnackbar(t("common.verification code has expired or incorrect."), {
            variant: "error",
            autoHideDuration: 1500,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          })
          return
        }
      });
    } finally {
      setLoading(false)
    }
  }
  const handleSendVerificationCode = async () => {
    try {
      const isEmailInvalid = !!errors.email;
      if (isEmailInvalid) return
      if (!getValues('email')) {
        enqueueSnackbar(t("common.Invalid email format"), {
          variant: "error",
          autoHideDuration: 1500,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        })
        return
      }
      setCodeLoading(true)
      const data = {
        "email": getValues('email'),
        "source": "NGTeco Office"
      }
      await UserService.sendVerifyCode(data).then((res) => {
        if (res.code == "00000000") {
          enqueueSnackbar(t("common.Verification code sent successfully."), {
            variant: "success",
            autoHideDuration: 1500,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          })
        } else if (res.code == 'MUASW0002') {
          enqueueSnackbar(t("common.verification email send too frequently"), {
            variant: "error",
            autoHideDuration: 1500,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          })
          return
        }
      });
    } finally {
      setCodeLoading(false)
    }
  }
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  };
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword)
  };
  return (
    <MainLayout image={<LayoutImage/>}>
      <Grid sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #CAD0D9",
        marginTop: "9vh",
        height: "76vh",
        minHeight: "580px",
        width: '34vw',
        borderRadius: "12px",
        position: "relative",
        backgroundColor: "#ffffff",
      }}>
        <LogoView/>
        {/* <Grid container={true} rowSpacing={2} component={"form"} noValidate sx={{width: "500px"}} onSubmit={handleSubmit(onForgotPwd)}> */}
        <Grid component={"form"} sx={{width: '28vw'}} onSubmit={handleSubmit(onForgotPwd)}>
          <Grid item xs={12} sx={{display: "inline-flex", flexDirection: "column", marginTop: "15px"}}>
            <Typography variant="h5" sx={{marginBottom: '15px'}}>{t("signUp.Forgot password?")}</Typography>
          </Grid>
          <Grid item xs={12} sx={{marginTop: "15px"}}>
            <NGInputLabel label={t("signUp.Enter the email")} required/>
            <TextField
              fullWidth={true}
              required={true}
              error={!!errors.email}
              helperText={errors.email?.message || ""}
              placeholder={t("signUp.Enter the email")}
              {...register("email", {onChange: () => trigger("email")})}
            />
          </Grid>
          <Grid item xs={12} sx={{marginTop: "15px"}}>
            <NGInputLabel label={t("signUp.Enter verification code")} required/>
            <Box sx={{display: 'flex', alignItems: 'stretch'}}>
              <TextField
                fullWidth
                required
                error={!!errors.verify_code}
                helperText={errors.verify_code?.message || ""}
                placeholder={t("signUp.Enter verification code")}
                {...register("verify_code")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,

                  },
                  '& .MuiFormHelperText-root': {
                    paddingLeft: 1,
                  }
                }}
              />

              <LoadingButton
                variant="outlined"
                color="primary"
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: 'none',
                  textTransform: 'none',
                  color: '#2BA9DE',
                  height: "%100",
                  minWidth: "140px",
                  borderColor: '#CAD0D9',
                  '&:hover': {
                    borderColor: '#2BA9DE',
                  }
                }}
                onClick={handleSendVerificationCode}
                disabled={codeLoading}
              >
                {t("signUp.Send Code")}
              </LoadingButton>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{marginTop: "15px"}}>
            <NGInputLabel label={t("signUp.New Password")} required/>
            <TextField
              fullWidth={true}
              required={true}
              error={!!errors.new_password}
              helperText={errors.new_password?.message || ""}
              placeholder={t("signUp.New Password")}
              type={showPassword ? 'text' : 'password'}
              inputProps={{'aria-label': 'description'}}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <VisibilityOutlinedIcon/> : <VisibilityOffOutlinedIcon/>}
                  </IconButton>
                </InputAdornment>
              }}
              {...register("new_password")}
            />
          </Grid>
          <Grid item xs={12} sx={{marginTop: "15px"}}>
            <NGInputLabel label={t("signUp.Confirm New Password")} required/>
            <TextField
              fullWidth={true}
              required={true}
              error={!!errors.confirm_password}
              helperText={errors.confirm_password?.message || ""}
              placeholder={t("signUp.Confirm New Password")}
              type={showConfirmPassword ? 'text' : 'password'}
              inputProps={{'aria-label': 'description'}}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={handleClickShowConfirmPassword}
                  >
                    {showConfirmPassword ? <VisibilityOutlinedIcon/> : <VisibilityOffOutlinedIcon/>}
                  </IconButton>
                </InputAdornment>
              }}
              {...register("confirm_password")}
            />
          </Grid>
          <Grid item xs={12} sx={{marginTop: "30px"}}>
            <LoadingButton
              sx={{textTransform: "none", backgroundColor: "#2BA9DE", marginTop: '24px'}}
              type={"submit"}
              fullWidth={true}
              variant={"contained"}
              loading={loading}
            >
              {t("common.confirm")}
            </LoadingButton>
          </Grid>
          <Grid item xs={12} sx={{
            display: "inline-flex",
            justifyContent: "center",
            paddingBottom: '24px',
            bottom: 0,
            position: "absolute",
            textAlign: 'center',
            width: '28vw',
          }}>
            <CustomLink sx={{color: '#2BA9DE'}}
                        label={t("login.Back to login")}
                        onClick={() => native(WebURL.AUTH_USER_LOGIN)}
            />
          </Grid>
        </Grid>
      </Grid>
    </MainLayout>
  )
}
export default ForgotPassword
